import { rm, access } from 'node:fs/promises';
import { join } from 'node:path';
import { createUIResource } from '@mcp-ui/server';
import { createLogger } from '../../logging/index.js';
import { JobService } from '../../services/job.service.js';
import { estimateIndexingTime } from '../../utils/eta.js';
import { estimateFileCount } from '../../utils/file-count.js';
import { checkStoreModelCompatibility } from '../../utils/model-validation.js';
import { spawnBackgroundWorker } from '../../workers/spawn-worker.js';
import {
  ListStoresArgsSchema,
  GetStoreInfoArgsSchema,
  CreateStoreArgsSchema,
  IndexStoreArgsSchema,
  DeleteStoreArgsSchema,
} from '../schemas/index.js';
import { renderStoresDashboard } from '../ui/stores-dashboard.js';
import type { Store, StoreType } from '../../types/store.js';
import type {
  ListStoresArgs,
  GetStoreInfoArgs,
  CreateStoreArgs,
  IndexStoreArgs,
  DeleteStoreArgs,
} from '../schemas/index.js';
import type { ToolHandler, ToolResponse } from '../types.js';

const logger = createLogger('mcp-store');

/**
 * Handle list_stores requests
 *
 * Lists all knowledge stores with optional type filtering.
 */
export const handleListStores: ToolHandler<ListStoresArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = ListStoresArgsSchema.parse(args);
  logger.info({ type: validated.type }, 'List stores started');

  const { services } = context;

  const stores = await services.store.list();
  const filtered =
    validated.type !== undefined ? stores.filter((s) => s.type === validated.type) : stores;

  logger.info({ count: filtered.length, type: validated.type }, 'List stores completed');

  const storeData = filtered.map((s) => ({
    id: s.id,
    name: s.name,
    type: s.type,
    path: 'path' in s ? s.path : undefined,
    url: 'url' in s && s.url !== undefined ? s.url : undefined,
    branch: 'branch' in s ? s.branch : undefined,
    depth: 'depth' in s ? s.depth : undefined,
    maxPages: 'maxPages' in s ? s.maxPages : undefined,
    tags: 'tags' in s ? s.tags : undefined,
    description: s.description,
    modelId: s.modelId,
    createdAt: s.createdAt.toISOString(),
  }));

  // UIResource type:'resource' is accepted by MCP SDK at runtime but not in its TS content union.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
  const uiResource = createUIResource({
    uri: 'ui://bk/stores',
    content: { type: 'rawHtml', htmlString: renderStoresDashboard(storeData) },
    encoding: 'text',
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const content = [
    { type: 'text' as const, text: JSON.stringify({ stores: storeData }, null, 2) },
    uiResource,
  ];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return { content };
};

/**
 * Handle get_store_info requests
 *
 * Retrieves detailed information about a specific store.
 */
export const handleGetStoreInfo: ToolHandler<GetStoreInfoArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = GetStoreInfoArgsSchema.parse(args);
  logger.info({ store: validated.store }, 'Get store info started');

  const { services } = context;

  const store = await services.store.getByIdOrName(validated.store);

  if (store === undefined) {
    logger.warn({ store: validated.store }, 'Store not found');
    throw new Error(`Store not found: ${validated.store}`);
  }

  logger.info({ storeId: store.id, storeName: store.name }, 'Get store info completed');

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            id: store.id,
            name: store.name,
            type: store.type,
            path: 'path' in store ? store.path : undefined,
            url: 'url' in store && store.url !== undefined ? store.url : undefined,
            branch: 'branch' in store ? store.branch : undefined,
            depth: 'depth' in store ? store.depth : undefined,
            maxPages: 'maxPages' in store ? store.maxPages : undefined,
            crawlInstructions: 'crawlInstructions' in store ? store.crawlInstructions : undefined,
            extractInstructions:
              'extractInstructions' in store ? store.extractInstructions : undefined,
            tags: 'tags' in store ? store.tags : undefined,
            description: store.description,
            modelId: store.modelId,
            status: store.status,
            createdAt: store.createdAt.toISOString(),
            updatedAt: store.updatedAt.toISOString(),
          },
          null,
          2
        ),
      },
    ],
  };
};

/**
 * Normalize a repo URL for duplicate detection.
 * Strips trailing .git suffix, trailing slash, and lowercases.
 */
function normalizeRepoUrl(url: string): string {
  return url
    .replace(/\.git$/, '')
    .replace(/\/$/, '')
    .toLowerCase();
}

/**
 * Handle create_store requests
 *
 * Creates a new knowledge store and starts background indexing.
 * Returns store info and job ID for tracking progress.
 */
export const handleCreateStore: ToolHandler<CreateStoreArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = CreateStoreArgsSchema.parse(args);
  logger.info(
    { name: validated.name, type: validated.type, source: validated.source },
    'Create store started'
  );

  const { services, options } = context;

  // Determine if source is a URL or path
  const isUrl =
    validated.source.startsWith('http://') ||
    validated.source.startsWith('https://') ||
    validated.source.startsWith('git@');

  // Check if this URL is already indexed under a different store name
  if (isUrl) {
    const stores = await services.store.list();
    const normalizedSource = normalizeRepoUrl(validated.source);
    const existing = stores.find(
      (s) => 'url' in s && s.url !== undefined && normalizeRepoUrl(s.url) === normalizedSource
    );
    if (existing !== undefined) {
      const storePath = 'path' in existing ? existing.path : undefined;
      logger.info(
        { name: validated.name, existingName: existing.name, url: validated.source },
        'Duplicate URL detected — returning existing store info'
      );
      return {
        content: [
          {
            type: 'text',
            text: JSON.stringify(
              {
                alreadyIndexed: true,
                store: {
                  id: existing.id,
                  name: existing.name,
                  type: existing.type,
                  path: storePath,
                  url: 'url' in existing ? existing.url : undefined,
                  createdAt: existing.createdAt.toISOString(),
                  updatedAt: existing.updatedAt.toISOString(),
                },
                search: {
                  tool: 'search',
                  example: { query: 'your question here', stores: [existing.name] },
                  tip:
                    storePath !== undefined
                      ? `Files also available for Grep/Read at: ${storePath}`
                      : undefined,
                },
                actions: {
                  reindex: `Use store:index ${existing.name} to re-index with current embeddings`,
                  pull: `Use stores:pull ${existing.name} to git pull latest and re-index`,
                  recreate: `Use store:delete ${existing.name} first, then store:create again for a fresh clone`,
                },
                message: `Repository already indexed as '${existing.name}' (last updated ${existing.updatedAt.toISOString()}). Use search tool with stores: ["${existing.name}"] to query it.`,
              },
              null,
              2
            ),
          },
        ],
      };
    }
  }

  const result = await services.store.create({
    name: validated.name,
    type: validated.type,
    ...(isUrl ? { url: validated.source } : { path: validated.source }),
    ...(validated.branch !== undefined ? { branch: validated.branch } : {}),
    ...(validated.description !== undefined ? { description: validated.description } : {}),
    ...(validated.tags !== undefined ? { tags: validated.tags } : {}),
    ...(validated.depth !== undefined ? { depth: validated.depth } : {}),
    ...(validated.maxPages !== undefined ? { maxPages: validated.maxPages } : {}),
    ...(validated.crawlInstructions !== undefined
      ? { crawlInstructions: validated.crawlInstructions }
      : {}),
    ...(validated.extractInstructions !== undefined
      ? { extractInstructions: validated.extractInstructions }
      : {}),
  });

  if (!result.success) {
    logger.error({ name: validated.name, error: result.error.message }, 'Create store failed');
    throw new Error(result.error.message);
  }

  // Create background job for indexing
  const jobService = new JobService(options.dataDir);
  const jobDetails: Record<string, unknown> = {
    storeName: result.data.name,
    storeId: result.data.id,
  };
  if (isUrl) {
    jobDetails['url'] = validated.source;
  }
  if ('path' in result.data && result.data.path) {
    jobDetails['path'] = result.data.path;
  }
  // Add crawl options for web stores
  if (validated.type === 'web') {
    if (validated.maxPages !== undefined) {
      jobDetails['maxPages'] = validated.maxPages;
    }
    if (validated.crawlInstructions !== undefined) {
      jobDetails['crawlInstruction'] = validated.crawlInstructions;
    }
    if (validated.extractInstructions !== undefined) {
      jobDetails['extractInstruction'] = validated.extractInstructions;
    }
  }

  // Determine job type and set initial phase
  const jobType =
    validated.type === 'web' ? 'crawl' : validated.type === 'repo' && isUrl ? 'clone' : 'index';

  // Set initial phase based on job type
  if (jobType === 'crawl') {
    jobDetails['phase'] = 'crawling';
    jobDetails['phaseStep'] = 1;
    jobDetails['phaseTotalSteps'] = 2;
  } else if (jobType === 'clone') {
    jobDetails['phase'] = 'cloning';
    jobDetails['phaseStep'] = 1;
    jobDetails['phaseTotalSteps'] = 2;
  } else {
    jobDetails['phase'] = 'indexing';
    jobDetails['phaseStep'] = 1;
    jobDetails['phaseTotalSteps'] = 1;
  }

  const job = jobService.createJob({
    type: jobType,
    details: jobDetails,
    message:
      validated.type === 'web'
        ? `Crawling ${result.data.name}...`
        : `Indexing ${result.data.name}...`,
  });

  // Spawn background worker
  spawnBackgroundWorker(job.id, options.dataDir);

  logger.info(
    { storeId: result.data.id, storeName: result.data.name, jobId: job.id },
    'Create store completed'
  );

  // Estimate file count and ETA for indexing
  const storePath = 'path' in result.data ? result.data.path : undefined;
  let fileCount = 0;
  if (validated.type === 'web') {
    fileCount = validated.maxPages ?? 0;
  } else if (storePath !== undefined) {
    fileCount = await estimateFileCount(storePath);
  }
  const eta = estimateIndexingTime(fileCount);
  const fileCountLabel = fileCount > 0 ? `~${String(fileCount)} files` : 'files';
  const jobMessage =
    fileCount > 0
      ? `Indexing ${result.data.name} (${fileCountLabel}, ${eta.etaDisplay})...`
      : job.message;

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            store: {
              id: result.data.id,
              name: result.data.name,
              type: result.data.type,
              path: storePath,
            },
            search: {
              immediate:
                storePath !== undefined
                  ? `Files available for Grep/Read at: ${storePath}`
                  : undefined,
              pending: `Similarity search indexing ${fileCountLabel}`,
              eta: eta.etaDisplay,
              monitor: `check job status with job:status ${job.id}`,
            },
            job: {
              id: job.id,
              status: job.status,
              message: jobMessage,
            },
            message:
              storePath !== undefined
                ? `Store created. Files available for Grep/Read now at ${storePath}. Similarity search indexing ${fileCountLabel} (${eta.etaDisplay}).`
                : `Store created. Similarity search indexing ${fileCountLabel} (${eta.etaDisplay}).`,
          },
          null,
          2
        ),
      },
    ],
  };
};

/**
 * Handle index_store requests
 *
 * Re-indexes an existing store in the background.
 * Returns job ID for tracking progress.
 */
export const handleIndexStore: ToolHandler<IndexStoreArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = IndexStoreArgsSchema.parse(args);
  logger.info({ store: validated.store }, 'Index store started');

  const { services, options } = context;

  const store = await services.store.getByIdOrName(validated.store);

  if (store === undefined) {
    logger.warn({ store: validated.store }, 'Store not found for indexing');
    throw new Error(`Store not found: ${validated.store}`);
  }

  // Create background job for indexing
  const jobService = new JobService(options.dataDir);
  const jobDetails: Record<string, unknown> = {
    storeName: store.name,
    storeId: store.id,
    // Index jobs are single-phase
    phase: 'indexing',
    phaseStep: 1,
    phaseTotalSteps: 1,
  };
  if ('path' in store && store.path) {
    jobDetails['path'] = store.path;
  }
  const job = jobService.createJob({
    type: 'index',
    details: jobDetails,
    message: `Re-indexing ${store.name}...`,
  });

  // Spawn background worker
  spawnBackgroundWorker(job.id, options.dataDir);

  logger.info({ storeId: store.id, storeName: store.name, jobId: job.id }, 'Index store completed');

  // Estimate file count and ETA
  const storePath = 'path' in store ? store.path : undefined;
  const fileCount = storePath !== undefined ? await estimateFileCount(storePath) : 0;
  const eta = estimateIndexingTime(fileCount);
  const fileCountLabel = fileCount > 0 ? `~${String(fileCount)} files` : 'files';
  const jobMessage =
    fileCount > 0
      ? `Re-indexing ${store.name} (${fileCountLabel}, ${eta.etaDisplay})...`
      : job.message;

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            store: {
              id: store.id,
              name: store.name,
            },
            search: {
              immediate:
                storePath !== undefined
                  ? `Files available for Grep/Read at: ${storePath}`
                  : undefined,
              pending: `Similarity search re-indexing ${fileCountLabel}`,
              eta: eta.etaDisplay,
              monitor: `check job status with job:status ${job.id}`,
            },
            job: {
              id: job.id,
              status: job.status,
              message: jobMessage,
            },
            message:
              storePath !== undefined
                ? `Re-indexing started. Files available for Grep/Read at ${storePath}. Similarity search indexing ${fileCountLabel} (${eta.etaDisplay}).`
                : `Re-indexing started. Similarity search indexing ${fileCountLabel} (${eta.etaDisplay}).`,
          },
          null,
          2
        ),
      },
    ],
  };
};

/**
 * Handle delete_store requests
 *
 * Deletes a store and all associated data:
 * - Removes from store registry
 * - Drops LanceDB table
 * - For repo stores with URL, removes cloned files
 */
export const handleDeleteStore: ToolHandler<DeleteStoreArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  // Validate arguments with Zod
  const validated = DeleteStoreArgsSchema.parse(args);
  logger.info({ store: validated.store }, 'Delete store started');

  const { services, options } = context;

  const store = await services.store.getByIdOrName(validated.store);

  if (store === undefined) {
    logger.warn({ store: validated.store }, 'Store not found for deletion');
    throw new Error(`Store not found: ${validated.store}`);
  }

  logger.debug({ storeId: store.id, storeName: store.name }, 'Deleting LanceDB table');
  // Delete LanceDB table first (so searches don't return results for deleted store)
  await services.lance.deleteStore(store.id);

  logger.debug({ storeId: store.id }, 'Deleting code graph');
  // Delete code graph file
  await services.codeGraph.deleteGraph(store.id);

  logger.debug({ storeId: store.id }, 'Deleting manifest');
  // Delete manifest file
  await services.manifest.delete(store.id);

  // For repo stores cloned from URL, remove the cloned directory
  if (store.type === 'repo' && 'url' in store && store.url !== undefined) {
    if (options.dataDir === undefined) {
      throw new Error('dataDir is required to delete cloned repository files');
    }
    const repoPath = join(options.dataDir, 'repos', store.id);
    logger.debug({ storeId: store.id, repoPath }, 'Removing cloned repository');
    await rm(repoPath, { recursive: true, force: true });
  }

  logger.debug({ storeId: store.id }, 'Removing from registry');
  // Delete from registry last
  const result = await services.store.delete(store.id);
  if (!result.success) {
    logger.error({ storeId: store.id, error: result.error.message }, 'Delete store failed');
    throw new Error(result.error.message);
  }

  logger.info(
    { storeId: store.id, storeName: store.name, storeType: store.type },
    'Delete store completed'
  );

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            deleted: true,
            store: {
              id: store.id,
              name: store.name,
              type: store.type,
            },
            message: `Successfully deleted store: ${store.name}`,
          },
          null,
          2
        ),
      },
    ],
  };
};

/**
 * Handle stores:check-models requests
 *
 * Lists all stores with their model compatibility status.
 * Identifies stores that need reindexing due to model mismatch.
 */
export const handleCheckModels: ToolHandler<Record<string, never>> = async (
  _args,
  context
): Promise<ToolResponse> => {
  logger.info('Check models started');

  const { services } = context;

  const stores = await services.store.list();
  const currentModelId = services.store.getCurrentModelId();

  const storeStatuses = stores.map((store) => {
    const compatibility = checkStoreModelCompatibility(store, { currentModelId });
    return {
      id: store.id,
      name: store.name,
      type: store.type,
      modelId: compatibility.modelId ?? '(unknown)',
      status: compatibility.compatible ? 'current' : 'needs-reindex',
      reason: compatibility.reason,
    };
  });

  const needsReindex = storeStatuses.filter((s) => s.status === 'needs-reindex');

  logger.info(
    { total: stores.length, needsReindex: needsReindex.length },
    'Check models completed'
  );

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            currentModel: currentModelId,
            stores: storeStatuses,
            summary: {
              total: stores.length,
              current: stores.length - needsReindex.length,
              needsReindex: needsReindex.length,
            },
            ...(needsReindex.length > 0
              ? {
                  reindexCommands: needsReindex.map((s) => `/bluera-knowledge:index ${s.name}`),
                }
              : {}),
          },
          null,
          2
        ),
      },
    ],
  };
};

/** Health check severity level */
type HealthSeverity = 'error' | 'warning';

/** Health check issue code */
type HealthIssueCode = 'PATH_NOT_FOUND' | 'SCHEMA_V1' | 'MODEL_MISMATCH';

/** A single health check issue */
interface HealthIssue {
  severity: HealthSeverity;
  code: HealthIssueCode;
  message: string;
  resolution: string;
}

/** Health check result for a single store */
interface HealthResult {
  storeId: string;
  storeName: string;
  storeType: StoreType;
  status: 'healthy' | 'warning' | 'error';
  issues: HealthIssue[];
}

/**
 * Check if a path exists
 */
async function pathExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check health of a single store
 */
async function checkStoreHealth(store: Store, currentModelId: string): Promise<HealthResult> {
  const issues: HealthIssue[] = [];

  // 1. Path check - file/repo only
  if (store.type === 'file' || store.type === 'repo') {
    const exists = await pathExists(store.path);
    if (!exists) {
      issues.push({
        severity: 'error',
        code: 'PATH_NOT_FOUND',
        message: `Path does not exist: ${store.path}`,
        resolution: 'Re-create store or fix projectRoot if path was relative',
      });
    }
  }

  // 2. Model compatibility check (reuses existing validation)
  const modelCheck = checkStoreModelCompatibility(store, { currentModelId });
  if (!modelCheck.compatible) {
    const isSchemaV1 = modelCheck.reason?.includes('v1') === true;
    issues.push({
      severity: 'warning',
      code: isSchemaV1 ? 'SCHEMA_V1' : 'MODEL_MISMATCH',
      message: modelCheck.reason ?? 'Model incompatibility',
      resolution: `Run: bluera-knowledge index ${store.name}`,
    });
  }

  // Determine overall status
  const hasError = issues.some((i) => i.severity === 'error');
  const hasWarning = issues.some((i) => i.severity === 'warning');
  const status = hasError ? 'error' : hasWarning ? 'warning' : 'healthy';

  return {
    storeId: store.id,
    storeName: store.name,
    storeType: store.type,
    status,
    issues,
  };
}

export interface StoreHealthArgs {
  store?: string;
}

/**
 * Handle stores:health requests
 *
 * Checks health of all stores: path existence, model compatibility.
 * Returns exit code for scripting: 0=healthy, 1=error, 2=warning.
 */
export const handleStoreHealth: ToolHandler<StoreHealthArgs> = async (
  args,
  context
): Promise<ToolResponse> => {
  logger.info({ store: args.store }, 'Store health check started');

  const { services } = context;
  const currentModelId = services.store.getCurrentModelId();

  let storesToCheck: Store[];

  if (args.store !== undefined) {
    const store = await services.store.getByIdOrName(args.store);
    if (store === undefined) {
      throw new Error(`Store not found: ${args.store}`);
    }
    storesToCheck = [store];
  } else {
    storesToCheck = await services.store.list();
  }

  // Check health of each store
  const results: HealthResult[] = [];
  for (const store of storesToCheck) {
    const result = await checkStoreHealth(store, currentModelId);
    results.push(result);
  }

  // Determine exit code: 1 for any error, 2 for warning-only, 0 for healthy
  const hasError = results.some((r) => r.status === 'error');
  const hasWarning = results.some((r) => r.status === 'warning');
  const exitCode = hasError ? 1 : hasWarning ? 2 : 0;

  // Summary counts
  const healthy = results.filter((r) => r.status === 'healthy').length;
  const warnings = results.filter((r) => r.status === 'warning').length;
  const errors = results.filter((r) => r.status === 'error').length;

  logger.info(
    { total: results.length, healthy, warnings, errors, exitCode },
    'Store health check completed'
  );

  return {
    content: [
      {
        type: 'text',
        text: JSON.stringify(
          {
            stores: results,
            summary: {
              total: results.length,
              healthy,
              warnings,
              errors,
            },
            exitCode,
          },
          null,
          2
        ),
      },
    ],
  };
};
